import React, { memo, useMemo, useCallback } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { PageHeader } from '@modules/common';
import { useBloc } from '@core/utils/bloc';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { HelpArticlesPageBloc } from './HelpArticlesPageBloc';
import { openAddEditHelpArticleModal } from '../AddEditHelpArticleModal';
import { openAddEditHelpArticleCategoryModal } from '../AddEditHelpArticleCategoryModal';
import { useRouteParam, useHistory } from '@core/router';
import { useObservable } from '@core/utils/hooks/rxjs';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { HelpArticleCard } from './HelpArticleCard';
import { useCompany } from '@core/utils/hooks/misc';

export const HelpArticlesPage = memo(() => {
  const categoryId = useRouteParam('id', true);
  const bloc = useBloc(HelpArticlesPageBloc, categoryId || '0');
  const categories = useObservable(bloc.categories$, []);
  const articles = useObservable(bloc.articles$, []);
  const loading = useLoadingState(bloc.loading);
  const company = useCompany();
  const history = useHistory();
  const isViewEnabled = useMemo(() => !!company.slug, [company]);

  const buttons = useMemo(() => {
    return [
      {
        title: 'Add Category',
        onClick: () => openAddEditHelpArticleCategoryModal({ categoryId }),
      },
      {
        title: 'Add Help Article',
        onClick: () => openAddEditHelpArticleModal({ categoryId }),
      },
    ];
  }, [categoryId]);

  const onView = useCallback(
    (type: string, itemSlug: string, categorySlug: string | undefined) => {
      if (type === 'category') {
        window.open(`/public/${company.slug}/${itemSlug}`, '_blank');
      } else {
        categorySlug = categorySlug ? `${categorySlug}` : 'uncategorized';
        const link = `/public/${company.slug}/${categorySlug}/${itemSlug}`;
        window.open(link, '_blank');
      }
    },
    [company],
  );

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>Manage Help Articles</PageHeader>
        <Row>
          {categories.map(category => (
            <HelpArticleCard
              onDelete={() => bloc.deleteCategory(category.id).subscribe()}
              onView={onView}
              key={`category-${category.id}`}
              item={category}
              type="category"
              isViewEnabled={isViewEnabled}
            />
          ))}
          {articles.map(article => (
            <HelpArticleCard
              onDelete={() => bloc.deleteHelpArticle(article.id).subscribe()}
              onView={onView}
              key={`article-${article.id}`}
              item={article}
              type="article"
              isViewEnabled={isViewEnabled}
            />
          ))}
        </Row>
      </Container>
    </div>
  );
});
